import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingCart, Store } from "lucide-react";

export default function CartPage() {
  const { cart, update } = useCart();
  const navigate = useNavigate();
  const items = cart.items || [];

  // group by store (multi-store cart → one order per store)
  const groups = {};
  items.forEach((it) => { (groups[it.store_id] = groups[it.store_id] || { name: it.store_name, items: [] }).items.push(it); });

  if (items.length === 0)
    return (
      <div className="text-center py-24">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4"><ShoppingCart className="w-7 h-7 text-muted-foreground" /></div>
        <h2 className="text-xl font-display font-bold">Your cart is empty</h2>
        <p className="text-muted-foreground text-sm mt-1">Browse stores and add products.</p>
        <Link to="/app"><Button className="mt-5 rounded-full bg-brand hover:bg-brand-dark">Discover stores</Button></Link>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-display font-bold">Your cart</h1>
      {Object.entries(groups).map(([storeId, g]) => {
        const subtotal = g.items.reduce((s, i) => s + i.price * i.quantity, 0);
        return (
          <div key={storeId} data-testid={`cart-store-${storeId}`} className="rounded-2xl border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/40">
              <Store className="w-4 h-4 text-brand" /><span className="font-semibold text-sm">{g.name}</span>
            </div>
            <div className="divide-y">
              {g.items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3 p-4">
                  <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0">{it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{it.name}</div>
                    <div className="text-brand font-semibold text-sm">{money(it.price, it.currency)}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button data-testid={`dec-${it.product_id}`} size="icon" variant="outline" className="h-7 w-7 rounded-lg" onClick={() => update(it.product_id, it.quantity - 1)}><Minus className="w-3 h-3" /></Button>
                    <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                    <Button data-testid={`inc-${it.product_id}`} size="icon" variant="outline" className="h-7 w-7 rounded-lg" onClick={() => update(it.product_id, it.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                    <Button data-testid={`rm-${it.product_id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-destructive" onClick={() => update(it.product_id, 0)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/20">
              <div><div className="text-xs text-muted-foreground">Subtotal</div><div className="font-display font-bold text-lg">{money(subtotal, g.items[0]?.currency)}</div></div>
              <Button data-testid={`checkout-${storeId}`} onClick={() => navigate(`/app/checkout/${storeId}`)} className="rounded-full bg-brand hover:bg-brand-dark">Checkout</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
